import type { PropsWithChildren } from "react";
import { useState } from "react";

import { Combobox } from "@similarweb/ui-components";

import type { OptionValue } from "./types";
import { Chip } from "./ui/Chip";

type SimpleSingleSelectFilterProps<K extends string> = {
    dataAutomation: string;
    onSelect: (itemId: K | null) => void;
    options: OptionValue<K>[];
    selectedValue: K | null;
};

export const SimpleSingleSelectFilter = <K extends string>({
    dataAutomation,
    onSelect,
    options,
    selectedValue,
    children,
}: PropsWithChildren<SimpleSingleSelectFilterProps<K>>) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (id: K) => {
        onSelect(id);
        setIsOpen(false);
    };

    const handleReset = () => {
        onSelect(null);
        setIsOpen(false);
    };

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={setIsOpen}
        >
            <Combobox.Trigger>
                <Chip
                    isSelected={!!selectedValue}
                    onReset={handleReset}
                    data-automation={dataAutomation}
                >
                    {children}
                </Chip>
            </Combobox.Trigger>

            <Combobox.Content>
                {options.map((item) => (
                    <Combobox.Option
                        key={item.id}
                        value={item}
                        onClick={() => handleSelect(item.id)}
                        textValue={item.id}
                        isSelected={selectedValue === item.id}
                    >
                        <Combobox.OptionLabel>{item.label}</Combobox.OptionLabel>
                    </Combobox.Option>
                ))}
            </Combobox.Content>
        </Combobox>
    );
};
